import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading, isAuthenticated, isAdmin, isCustomer, isManager, isCustomerManager, isSalesExecutive, isEmployee } = useAuth();
  const location = useLocation();

  // Only log when there's an authentication issue or access check
  if (!isAuthenticated && !loading) {
    console.log('ProtectedRoute - Not authenticated, redirecting to login');
  } else if (isAuthenticated && requiredRole) {
    console.log('ProtectedRoute - Access check:', {
      requiredRole,
      userRole: user?.role,
      hasAccess:
        (requiredRole === 'admin' && isAdmin) ||
        (requiredRole === 'customer' && isCustomer) ||
        (requiredRole === 'manager' && isManager) ||
        (requiredRole === 'customer_manager' && isCustomerManager) ||
        (requiredRole === 'sales_executive' && isSalesExecutive) ||
        (requiredRole === 'employee' && isEmployee)
    });
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to appropriate login page based on the route
    const isCustomerRoute = location.pathname.startsWith('/customer');
    const isManagerRoute = location.pathname.startsWith('/manager');
    const isCustomerManagerRoute = location.pathname.startsWith('/manager-dashboard');
    const isEmployeeRoute = location.pathname.startsWith('/employee');

    let loginPath = '/';
    if (isCustomerRoute) {
      loginPath = '/';
    } else if (isManagerRoute) {
      loginPath = '/manager/login';
    } else if (isCustomerManagerRoute) {
      loginPath = '/customer-manager/login';
    } else if (isEmployeeRoute) {
      loginPath = '/login';
    }

    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Role-based access control with fixed logic
  if (requiredRole === 'admin' && !isAdmin) {
    console.log('User is not admin, redirecting to appropriate dashboard');
    if (isManager) {
      return <Navigate to="/manager" replace />;
    } else if (isCustomer) {
      return <Navigate to="/customer/dashboard" replace />;
    } else if (isCustomerManager) {
      return <Navigate to="/manager-dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'customer' && !isCustomer) {
    console.log('User is not customer, redirecting to appropriate dashboard');
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (isManager) {
      return <Navigate to="/manager" replace />;
    } else if (isCustomerManager) {
      return <Navigate to="/manager-dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'manager' && !isManager) {
    console.log('User is not manager, redirecting to appropriate dashboard');
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (isCustomer) {
      return <Navigate to="/customer/dashboard" replace />;
    } else if (isCustomerManager) {
      return <Navigate to="/manager-dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'customer_manager' && !isCustomerManager) {
    console.log('User is not customer manager, redirecting to appropriate dashboard');
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (isManager) {
      return <Navigate to="/manager" replace />;
    } else if (isCustomer) {
      return <Navigate to="/customer/dashboard" replace />;
    } else if (isSalesExecutive) {
      return <Navigate to="/salesman/dashboard" replace />;
    }
    return <Navigate to="/customer-manager/login" replace />;
  }

  if (requiredRole === 'sales_executive' && !isSalesExecutive) {
    console.log('User is not sales executive, redirecting to appropriate dashboard');
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (isManager) {
      return <Navigate to="/manager" replace />;
    } else if (isCustomer) {
      return <Navigate to="/customer/dashboard" replace />;
    } else if (isCustomerManager) {
      return <Navigate to="/manager-dashboard" replace />;
    } else if (isEmployee) {
      return <Navigate to="/employee/dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'employee' && !isEmployee) {
    console.log('User is not employee, redirecting to appropriate dashboard');
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (isManager) {
      return <Navigate to="/manager" replace />;
    } else if (isCustomer) {
      return <Navigate to="/customer/dashboard" replace />;
    } else if (isCustomerManager) {
      return <Navigate to="/manager-dashboard" replace />;
    } else if (isSalesExecutive) {
      return <Navigate to="/salesman/dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute; 